import { useRef, useState, type RefObject } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDeleteIssue } from "@/hooks/use-issue-mutations";
import { ApiError, OfflineError } from "@/lib/api";
import { formatIssueNumber } from "@/lib/issues";
import type { ProjectIssue } from "@/types/api";

/** What the user must type. Compared trimmed and case-folded. */
export const CONFIRM_PHRASE = "delete";

interface DeleteIssueDialogProps {
  projectId: string;
  issue: ProjectIssue;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called after a successful delete instead of navigating back to the issues
   * list — the Issues page's side panel closes itself, keeping the list's
   * filters in the URL.
   */
  onDeleted?: () => void;
}

/**
 * Type-to-confirm deletion of one issue. See [[delete-issue-dialog.spec]].
 *
 * Structured like [[issue-edit-sheet.spec]]: the chrome is here, the state and
 * the hooks live in the body, and the body mounts only while the dialog is
 * open (Radix unmounts the content when closed) and is keyed on `open`. So
 * closing by any route — Cancel, Escape, the overlay — throws the typed text
 * and any error away, and a page that mounts this dialog closed pays for none
 * of its hooks.
 */
export function DeleteIssueDialog({
  projectId,
  issue,
  open,
  onOpenChange,
  onDeleted,
}: DeleteIssueDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        onOpenAutoFocus={(e) => {
          // Radix's default onOpenAutoFocus moves focus to Cancel; win the
          // race by preventing that and focusing the confirm input instead.
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <DeleteIssueForm
          key={open ? "open" : "closed"}
          projectId={projectId}
          issue={issue}
          inputRef={inputRef}
          onDeleted={onDeleted}
        />
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DeleteIssueForm({
  projectId,
  issue,
  inputRef,
  onDeleted,
}: {
  projectId: string;
  issue: ProjectIssue;
  inputRef: RefObject<HTMLInputElement | null>;
  onDeleted?: () => void;
}) {
  const navigate = useNavigate();
  const deleteIssue = useDeleteIssue();
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const canDelete = confirmText.trim().toLowerCase() === CONFIRM_PHRASE;
  const isPending = deleteIssue.isPending;

  const handleDelete = () => {
    if (!canDelete || isPending) return;
    setError(null);
    deleteIssue.mutate(
      { projectId, issueId: issue.id, recordId: issue.record },
      {
        onSuccess: () => {
          toast("Issue deleted");
          if (onDeleted) {
            onDeleted();
            return;
          }
          // Replace, not push: Back must not land on a 404 for the issue that
          // was just removed.
          void navigate({
            to: "/projects/$projectId/issues",
            params: { projectId },
            replace: true,
          });
        },
        onError: (err) => {
          // The dialog stays open with the reason under the input, so a retry
          // costs no retyping. Copy is discriminated by kind and never echoes
          // `err.message` — Safari's "Load failed" is not something to show a
          // superintendent.
          if (err instanceof OfflineError) {
            setError("Offline — reconnect to delete this issue");
            return;
          }
          setError(
            err instanceof ApiError
              ? "Couldn't delete this issue — please try again"
              : "Couldn't delete — check your connection and try again",
          );
          // `captureMessage`, not `captureException`, so the report survives
          // `instrument.ts`'s `ignoreErrors` for fetch-failure messages. See
          // `specs/conventions/error-reporting.md`. Metadata only.
          Sentry.captureMessage("Delete issue request failed", {
            level: "error",
            tags: { area: "issue-delete" },
            extra: {
              projectId,
              issueId: issue.id,
              errorName: err instanceof Error ? err.name : "Unknown",
              errorMessage: err instanceof Error ? err.message : String(err),
              status: err instanceof ApiError ? err.status : undefined,
            },
          });
        },
      },
    );
  };

  return (
    <>
      <AlertDialogHeader>
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-destructive/15 bg-destructive/[0.08]">
          <AlertTriangle
            className="h-5.5 w-5.5 text-destructive"
            aria-hidden="true"
          />
        </div>
        <AlertDialogTitle className="text-center">Delete issue</AlertDialogTitle>
        <AlertDialogDescription className="text-center">
          This removes{" "}
          <span className="font-semibold text-foreground">
            {`${formatIssueNumber(issue.number)} — ${issue.title}`}
          </span>
          , its updates and its fix photos from the project for everyone. This
          can&apos;t be undone.
        </AlertDialogDescription>
      </AlertDialogHeader>

      <div className="space-y-2 py-2">
        <Label htmlFor="confirm-delete-issue" className="text-sm">
          Type{" "}
          <span className="font-mono font-semibold text-destructive">
            {CONFIRM_PHRASE}
          </span>{" "}
          to confirm:
        </Label>
        {/* autoCapitalize / autoCorrect / spellCheck off: iOS capitalises the
            first letter and autocorrect can rewrite the word, and either would
            leave Delete dead with no visible reason. */}
        <Input
          ref={inputRef}
          id="confirm-delete-issue"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={CONFIRM_PHRASE}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
        />
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>

      <AlertDialogFooter>
        <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
        {/* A plain Button, not AlertDialogAction: the action must not close the
            dialog on click, so a wrong phrase or a failed request leaves the
            user exactly where they were. */}
        <Button
          variant="destructive"
          onClick={handleDelete}
          disabled={!canDelete || isPending}
          loading={isPending}
        >
          Delete
        </Button>
      </AlertDialogFooter>
    </>
  );
}
